import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Button } from "@/components/PageUI";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Star, Lock } from "lucide-react";
import { SecuritySection } from "@/components/SecuritySection";
import { SplashPreview } from "@/components/SplashPreview";
import AvatarUpload from "@/components/AvatarUpload";
import { compressImage } from "@/lib/image";

const StarMini = ({ value }) => (
  <span className="inline-flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={12} className={n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"} />)}</span>
);

const ProBadge = () => (
  <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-primary/15 text-primary rounded-full px-2 py-0.5" data-testid="pro-badge"><Lock size={11} /> Pro/Club</span>
);

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "INR", "BRL", "MXN", "ZAR", "JPY"];
const LANGUAGES = ["English", "Spanish", "French", "German", "Portuguese", "Italian", "Hindi", "Arabic"];
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Los_Angeles", "Europe/London", "Europe/Madrid", "Asia/Kolkata", "Australia/Sydney"];

const ChipEditor = ({ title, testid, value, defaults, onChange }) => {
  const [input, setInput] = useState("");
  const list = value && value.length ? value : (defaults || []);
  const add = () => { const v = input.trim(); if (!v || list.includes(v)) { setInput(""); return; } onChange([...list, v]); setInput(""); };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <Label>{title}</Label>
        {defaults && <button type="button" onClick={() => onChange([...defaults])} className="text-xs text-secondary hover:underline" data-testid={`${testid}-reset`}>Reset to defaults</button>}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {list.map((chip, i) => (
          <span key={`${chip}-${i}`} className="inline-flex items-center gap-1.5 bg-muted rounded-full pl-3 pr-1.5 py-1 text-sm" data-testid={`${testid}-chip-${i}`}>
            {chip}
            <button type="button" onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive rounded-full h-5 w-5 inline-flex items-center justify-center leading-none">×</button>
          </span>
        ))}
        {list.length === 0 && <span className="text-sm text-muted-foreground">None yet — add some below.</span>}
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={`Add ${title.toLowerCase()}…`} className="h-9" data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={add} data-testid={`${testid}-add`}>Add</Button>
      </div>
    </div>
  );
};

export default function Settings() {
  const { user, club, loadClub, isPremium, sportMeta, logout } = useAuth();
  const [sports, setSports] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const isOwner = user && club && club.owner_id === user.id;

  const deleteAccount = async () => {
    if (delConfirm.trim() !== "DELETE") return;
    setDeleting(true);
    try {
      await api.delete("/account");
      toast.success("Your account has been deleted");
      await logout();
      window.location.href = "/";
    } catch (e) { toast.error(apiErr(e)); setDeleting(false); }
  };

  useEffect(() => {
    api.get("/meta/sports").then((r) => setSports(r.data.sports));
    if (club) setForm({ ...club });
  }, [club]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onLogo = (url) => { if (url) set("logo", url); };
  const onHero = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    set("hero_photo", await compressImage(file, { maxDim: 1280, quality: 0.85 }));
  };
  const [testimonials, setTestimonials] = useState([]);
  const [tstForm, setTstForm] = useState({ author: "", role: "", quote: "", rating: 0 });
  useEffect(() => { api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {}); }, []);
  const addTestimonial = async () => {
    if (!tstForm.author || !tstForm.quote) return toast.error("Author and quote are required");
    try {
      await api.post("/testimonials", tstForm);
      setTstForm({ author: "", role: "", quote: "", rating: 0 });
      api.get("/testimonials").then((r) => setTestimonials(r.data));
      toast.success("Testimonial added");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delTestimonial = async (id) => { await api.delete(`/testimonials/${id}`); setTestimonials((l) => l.filter((x) => x.id !== id)); };
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyEdit, setReplyEdit] = useState({});
  const saveReply = async (id) => {
    const text = (replyEdit[id] ?? "").trim();
    try {
      await api.put(`/testimonials/${id}/reply`, { reply: text });
      setTestimonials((l) => l.map((t) => (t.id === id ? { ...t, reply: text } : t)));
      setReplyOpen(null);
      toast.success(text ? "Reply saved" : "Reply removed");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const [dragIdx, setDragIdx] = useState(null);
  const onDropTst = async (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const arr = testimonials.filter((t) => t.approved !== false);
    const [moved] = arr.splice(dragIdx, 1); arr.splice(idx, 0, moved);
    setTestimonials([...arr, ...testimonials.filter((t) => t.approved === false)]); setDragIdx(null);
    try { await api.put("/testimonials/reorder", { ids: arr.map((t) => t.id) }); } catch (e) { console.warn("Reorder failed to save", e); }
  };
  const [gallery, setGallery] = useState([]);
  const [galCaption, setGalCaption] = useState("");
  useEffect(() => { api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {}); }, []);
  const onGalleryImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    const image = await compressImage(file, { maxDim: 1280, quality: 0.85 });
    try { await api.post("/gallery", { image, caption: galCaption }); setGalCaption(""); api.get("/gallery").then((r) => setGallery(r.data)); toast.success("Photo added"); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const delGallery = async (id) => { await api.delete(`/gallery/${id}`); setGallery((l) => l.filter((x) => x.id !== id)); };
  const [galDrag, setGalDrag] = useState(null);
  const onDropGal = async (idx) => {
    if (galDrag === null || galDrag === idx) return;
    const arr = [...gallery];
    const [moved] = arr.splice(galDrag, 1); arr.splice(idx, 0, moved);
    setGallery(arr); setGalDrag(null);
    try { await api.put("/gallery/reorder", { ids: arr.map((g) => g.id) }); } catch (e) { console.warn("Reorder failed to save", e); }
  };
  const approveTestimonial = async (id) => {
    await api.put(`/testimonials/${id}/approve`);
    setTestimonials((l) => l.map((t) => (t.id === id ? { ...t, approved: true } : t)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { plan, subscription_active, id, owner_id, created_at, ...payload } = form;
      await api.put("/clubs/me", payload);
      await loadClub();
      toast.success("Club settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!form) return <Loader />;

  return (
    <div className="animate-fade-up max-w-3xl">
      <PageHeader title="Club Settings" subtitle="Customize your branded club experience." testid="settings-header"
        action={<Button onClick={save} disabled={saving} className="brand-gradient text-white border-0 hover:opacity-90" data-testid="save-settings-btn">{saving ? "Saving..." : "Save changes"}</Button>} />

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-6 mb-5" data-testid="sport-config-card">
        <div>
          <h3 className="font-heading font-semibold text-lg">Positions &amp; stats</h3>
          <p className="text-sm text-muted-foreground mt-1">Tailor positions, match stats and rating categories to how your {sportMeta?.label || "sport"} club actually plays. These power your roster, match stats and player ratings across the app.</p>
        </div>
        <ChipEditor title="Positions" testid="cfg-positions" value={form.custom_positions} defaults={sportMeta?.defaults?.positions} onChange={(v) => set("custom_positions", v)} />
        <ChipEditor title="Match stats" testid="cfg-stats" value={form.custom_stats} defaults={sportMeta?.defaults?.stats} onChange={(v) => set("custom_stats", v)} />
        <ChipEditor title="Rating categories" testid="cfg-ratings" value={form.custom_rating_categories} defaults={sportMeta?.defaults?.rating_categories} onChange={(v) => set("custom_rating_categories", v)} />
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-5">
        <AvatarUpload value={form.logo} onFile={onLogo} testid="settings-logo-input" label="Club logo" />
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2"><Label>Club name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11" data-testid="settings-name-input" /></div>
          <div><Label>Sport</Label>
            <Select value={form.sport} onValueChange={(v) => set("sport", v)}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="settings-sport-select"><SelectValue /></SelectTrigger>
              <SelectContent>{sports.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Country</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5 h-11" /></div>
          <div><Label>Currency</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="settings-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Language</Label>
            <Select value={form.language} onValueChange={(v) => set("language", v)}>
              <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGUAGES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Time zone</Label>
            <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
              <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Website</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} className="mt-1.5 h-11" /></div>
          <div className="flex gap-4">
            <div><Label className="flex items-center gap-2">Primary {!isPremium && <ProBadge />}</Label><input type="color" value={form.primary_color} disabled={!isPremium} onChange={(e) => set("primary_color", e.target.value)} className="mt-1.5 h-11 w-full rounded-lg bg-transparent border border-border cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" data-testid="settings-primary-color" /></div>
            <div><Label className="flex items-center gap-2">Accent {!isPremium && <ProBadge />}</Label><input type="color" value={form.secondary_color} disabled={!isPremium} onChange={(e) => set("secondary_color", e.target.value)} className="mt-1.5 h-11 w-full rounded-lg bg-transparent border border-border cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" data-testid="settings-accent-color" /></div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-4 mt-5" data-testid="public-site-card">
        <div>
          <h3 className="font-heading font-semibold text-lg">Public club website</h3>
          <p className="text-sm text-muted-foreground mt-1">A shareable page with your schedule, teams and a join link to attract new families.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>About your club</Label><textarea value={form.about || ""} onChange={(e) => set("about", e.target.value)} rows={3} placeholder="Tell families what makes your club special..." className="mt-1.5 w-full rounded-lg bg-background border border-border p-3 text-sm" data-testid="settings-about-input" /></div>
          <div><Label>Contact email</Label><Input value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="mt-1.5 h-11" data-testid="settings-contact-email" /></div>
          <div><Label>Contact phone</Label><Input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="mt-1.5 h-11" data-testid="settings-contact-phone" /></div>
        </div>
        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={!!form.roster_public} onChange={(e) => set("roster_public", e.target.checked)} className="h-4 w-4 accent-primary" data-testid="roster-public-toggle" />
          Show athlete roster on the public page
        </label>
        <label className={`flex items-center gap-3 text-sm ${isPremium ? "cursor-pointer" : "opacity-60"}`} data-testid="require-card-label">
          <input type="checkbox" disabled={!isPremium} checked={!!form.require_card_on_registration} onChange={(e) => set("require_card_on_registration", e.target.checked)} className="h-4 w-4 accent-primary" data-testid="require-card-toggle" />
          Require a payment card during registration (securely saved via Stripe for future autopay) {!isPremium && <ProBadge />}
        </label>
        <label className={`flex items-center gap-3 text-sm ${isPremium ? "cursor-pointer" : "opacity-60"}`} data-testid="auto-charge-label">
          <input type="checkbox" disabled={!isPremium} checked={form.auto_charge_fees !== false} onChange={(e) => set("auto_charge_fees", e.target.checked)} className="h-4 w-4 accent-primary" data-testid="auto-charge-toggle" />
          Auto-charge saved cards when I create a fee — no waiting for parent approval {!isPremium && <ProBadge />}
        </label>
        <label className={`flex items-center gap-3 text-sm ${isPremium ? "cursor-pointer" : "opacity-60"}`} data-testid="overdue-nudge-label">
          <input type="checkbox" disabled={!isPremium} checked={!!form.overdue_nudge_enabled} onChange={(e) => set("overdue_nudge_enabled", e.target.checked)} className="h-4 w-4 accent-primary" data-testid="overdue-nudge-toggle" />
          Automatically email families with overdue fees every week {!isPremium && <ProBadge />}
        </label>
        <div className="rounded-xl border border-border bg-background/50 p-4 space-y-3" data-testid="payment-methods-card">
          <p className="font-medium text-sm">Payment methods & processing fees</p>
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.bank_debit_enabled} onChange={(e) => set("bank_debit_enabled", e.target.checked)} className="h-4 w-4 mt-0.5 accent-primary" data-testid="bank-debit-toggle" />
            <span>Offer bank direct debit (ACH / BACS / SEPA / BECS) alongside cards<span className="block text-xs text-muted-foreground">Roughly 0.8% capped at 5.00 vs 2.9% + 0.30 for cards. Available for USD, GBP, EUR and AUD; settles in 3–5 business days.</span></span>
          </label>
          <p className="text-xs text-muted-foreground" data-testid="fees-policy-note">SphereHyx charges <b>no platform fee</b>. Only Stripe's processing fee (≈2.9% + 0.30 on cards, ≈0.8% capped on bank debit) is added at checkout and covered by the athlete / parent — so your club always receives the full amount.</p>
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <input type="checkbox" checked={form.wallet_enabled !== false} onChange={(e) => set("wallet_enabled", e.target.checked)} className="h-4 w-4 mt-0.5 accent-primary" data-testid="wallet-toggle" />
            <span>Enable prepaid club wallet (recommended for small fees)<span className="block text-xs text-muted-foreground">Families top up a balance once, then pay fees, tournaments and store items with zero extra processing fees — so a $5 payment no longer loses ~7% to Stripe's fixed per-transaction fee.</span></span>
          </label>
        </div>
        <div className="flex items-center gap-4">
          {form.hero_photo ? <img src={form.hero_photo} alt="" className="h-16 w-24 rounded-lg object-cover border border-border" /> : <div className="h-16 w-24 rounded-lg brand-gradient" />}
          <div className="flex-1"><Label>Hero / team photo</Label><Input type="file" accept="image/*" onChange={onHero} className="mt-1.5" data-testid="settings-hero-input" /><p className="text-xs text-muted-foreground mt-1">Shown as the banner on your public club page.</p></div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <Input readOnly value={`${window.location.origin}/club/${club?.id}`} className="h-10 font-mono text-xs" data-testid="public-site-link" />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/club/${club?.id}`); toast.success("Link copied"); }} data-testid="copy-site-link-btn">Copy</Button>
            <Button variant="outline" onClick={() => window.open(`/club/${club?.id}`, "_blank")} data-testid="view-site-btn">View</Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Save your changes first for the toggle to take effect on the public page.</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-4 mt-5" data-testid="latefee-card">
        <div>
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2">Late-fee automation {!isPremium && <ProBadge />}</h3>
          <p className="text-sm text-muted-foreground mt-1">Automatically flag overdue fees and add a small late charge after the grace period.</p>
        </div>
        <label className={`flex items-center gap-3 text-sm ${isPremium ? "cursor-pointer" : "opacity-60"}`}>
          <input type="checkbox" disabled={!isPremium} checked={!!form.late_fee_enabled} onChange={(e) => set("late_fee_enabled", e.target.checked)} className="h-4 w-4 accent-primary" data-testid="latefee-toggle" />
          Enable automatic late fees
        </label>
        {form.late_fee_enabled && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Late fee amount ({form.currency || "USD"})</Label><Input type="number" value={form.late_fee_amount ?? ""} onChange={(e) => set("late_fee_amount", e.target.value === "" ? "" : +e.target.value)} className="mt-1.5 h-11" data-testid="latefee-amount" /></div>
            <div><Label>Grace period (days after due)</Label><Input type="number" value={form.late_fee_grace_days ?? ""} onChange={(e) => set("late_fee_grace_days", e.target.value === "" ? "" : +e.target.value)} className="mt-1.5 h-11" data-testid="latefee-grace" /></div>
          </div>
        )}
      </div>

      <SecuritySection />

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 mt-5" data-testid="splash-preview-card">
        <div className="mb-5">
          <h3 className="font-heading font-semibold text-lg">How your installed app looks</h3>
          <p className="text-sm text-muted-foreground mt-1">The branded launch screen members see when they install SphereHyx to their home screen — no need to install it yourself to preview.</p>
        </div>
        <SplashPreview />
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-4 mt-5" data-testid="review-requests-card">
        <div>
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2">Review requests {!isPremium && <ProBadge />}</h3>
          <p className="text-sm text-muted-foreground mt-1">Invite families to leave a review by email — a great way to build social proof on your public page.</p>
        </div>
        <label className={`flex items-center gap-3 text-sm ${isPremium ? "cursor-pointer" : "opacity-60"}`}>
          <input type="checkbox" disabled={!isPremium} checked={!!form.review_requests_enabled} onChange={(e) => set("review_requests_enabled", e.target.checked)} className="h-4 w-4 accent-primary" data-testid="review-requests-toggle" />
          Automatically email a review invite once a member has been with the club for a while
        </label>
        {form.review_requests_enabled && (
          <div className="sm:max-w-xs"><Label>Send after (days since joining)</Label><Input type="number" value={form.review_request_days ?? ""} onChange={(e) => set("review_request_days", e.target.value === "" ? "" : +e.target.value)} className="mt-1.5 h-11" data-testid="review-request-days" /></div>
        )}
        <div className="flex items-center gap-3 pt-1 flex-wrap">
          <Button variant="outline" size="sm" onClick={async () => {
            try { const r = await api.post("/admin/run-review-requests"); toast.success(`Sent ${r.data.invites_sent} review invite${r.data.invites_sent === 1 ? "" : "s"}`); }
            catch (e) { toast.error(apiErr(e)); }
          }} data-testid="send-review-invites-btn">Send invites now</Button>
          <span className="text-xs text-muted-foreground">Emails every family who hasn't been invited yet.</span>
        </div>
      </div>

      {testimonials.some((t) => t.approved === false) && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5 sm:p-7 space-y-4 mt-5" data-testid="review-moderation-card">
          <div>
            <h3 className="font-heading font-semibold text-lg flex items-center gap-2">Pending reviews <span className="text-xs font-normal bg-amber-400/20 text-amber-500 rounded-full px-2 py-0.5" data-testid="pending-count">{testimonials.filter((t) => t.approved === false).length}</span></h3>
            <p className="text-sm text-muted-foreground mt-1">New reviews submitted by families on your public page. Approve to publish, or reject to discard.</p>
          </div>
          <div className="divide-y divide-border">
            {testimonials.filter((t) => t.approved === false).map((t) => (
              <div key={t.id} className="flex items-start justify-between gap-3 py-3" data-testid={`pending-row-${t.id}`}>
                <div className="min-w-0">
                  {t.rating > 0 && <div className="mb-1"><StarMini value={t.rating} /></div>}
                  <p className="text-sm">"{t.quote}"</p>
                  <p className="text-xs text-muted-foreground mt-1">— {t.author}{t.role ? `, ${t.role}` : ""}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => approveTestimonial(t.id)} className="text-secondary hover:underline text-xs font-semibold" data-testid={`approve-review-${t.id}`}>Approve</button>
                  <button onClick={() => { delTestimonial(t.id); toast.success("Review rejected"); }} className="text-muted-foreground hover:text-destructive text-xs" data-testid={`reject-review-${t.id}`}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-4 mt-5" data-testid="testimonials-card">
        <div>
          <h3 className="font-heading font-semibold text-lg">Testimonials</h3>
          <p className="text-sm text-muted-foreground mt-1">Short quotes from families, shown on your public club page. Drag to reorder.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={tstForm.author} onChange={(e) => setTstForm({ ...tstForm, author: e.target.value })} placeholder="Author (e.g. Sarah M.)" data-testid="tst-author-input" />
          <Input value={tstForm.role} onChange={(e) => setTstForm({ ...tstForm, role: e.target.value })} placeholder="Role (e.g. Parent) — optional" data-testid="tst-role-input" />
        </div>
        <textarea value={tstForm.quote} onChange={(e) => setTstForm({ ...tstForm, quote: e.target.value })} rows={2} placeholder="Their quote..." className="w-full rounded-lg bg-background border border-border p-3 text-sm" data-testid="tst-quote-input" />
        <div className="flex items-center gap-1" data-testid="tst-rating-picker">
          <span className="text-sm text-muted-foreground mr-1">Rating (optional)</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button type="button" key={n} onClick={() => setTstForm({ ...tstForm, rating: tstForm.rating === n ? 0 : n })} data-testid={`tst-star-${n}`} className="p-0.5">
              <Star size={20} className={n <= tstForm.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"} />
            </button>
          ))}
        </div>
        <Button size="sm" onClick={addTestimonial} className="brand-gradient text-white border-0" data-testid="add-testimonial-btn">Add testimonial</Button>
        {testimonials.filter((t) => t.approved !== false).length > 0 && (
          <div className="divide-y divide-border pt-2">
            {testimonials.filter((t) => t.approved !== false).map((t, i) => (
              <div key={t.id} className="py-3" data-testid={`testimonial-row-${t.id}`}>
                <div draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropTst(i)} className="flex items-start justify-between gap-3 cursor-move">
                  <div className="flex items-start gap-2"><span className="text-muted-foreground mt-0.5 select-none">⠿</span><div>{t.rating > 0 && <div className="mb-1"><StarMini value={t.rating} /></div>}<p className="text-sm">"{t.quote}"</p><p className="text-xs text-muted-foreground mt-1">— {t.author}{t.role ? `, ${t.role}` : ""}</p></div></div>
                  <button onClick={() => delTestimonial(t.id)} className="text-muted-foreground hover:text-destructive text-xs shrink-0" data-testid={`del-testimonial-${t.id}`}>Delete</button>
                </div>
                <div className="ml-6 mt-2">
                  {replyOpen === t.id ? (
                    <div className="space-y-2">
                      <textarea value={replyEdit[t.id] ?? t.reply ?? ""} onChange={(e) => setReplyEdit({ ...replyEdit, [t.id]: e.target.value })} rows={2} placeholder="Write a short public reply..." className="w-full rounded-lg bg-background border border-border p-2 text-sm" data-testid={`reply-input-${t.id}`} />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveReply(t.id)} className="brand-gradient text-white border-0" data-testid={`save-reply-${t.id}`}>Save reply</Button>
                        <Button size="sm" variant="outline" onClick={() => setReplyOpen(null)} data-testid={`cancel-reply-${t.id}`}>Cancel</Button>
                      </div>
                    </div>
                  ) : t.reply ? (
                    <div className="rounded-lg bg-muted/40 border border-border p-2.5" data-testid={`reply-display-${t.id}`}>
                      <p className="text-[11px] uppercase tracking-wide text-secondary font-semibold mb-0.5">Your response</p>
                      <p className="text-sm text-muted-foreground">{t.reply}</p>
                      <button onClick={() => { setReplyEdit({ ...replyEdit, [t.id]: t.reply }); setReplyOpen(t.id); }} className="text-xs text-secondary hover:underline mt-1" data-testid={`edit-reply-${t.id}`}>Edit reply</button>
                    </div>
                  ) : (
                    <button onClick={() => { setReplyEdit({ ...replyEdit, [t.id]: "" }); setReplyOpen(t.id); }} className="text-xs text-secondary hover:underline" data-testid={`reply-btn-${t.id}`}>+ Reply publicly</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-4 mt-5" data-testid="gallery-card">
        <div>
          <h3 className="font-heading font-semibold text-lg">Photo gallery</h3>
          <p className="text-sm text-muted-foreground mt-1">Event and team photos shown on your public club page.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 items-end">
          <div><Label>Caption (optional)</Label><Input value={galCaption} onChange={(e) => setGalCaption(e.target.value)} placeholder="e.g. 2026 Spring Cup champions" className="mt-1.5" data-testid="gallery-caption-input" /></div>
          <div><Label>Add photo</Label><Input type="file" accept="image/*" onChange={onGalleryImage} className="mt-1.5" data-testid="gallery-upload-input" /></div>
        </div>
        {gallery.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 pt-2">
            {gallery.map((g, i) => (
              <div key={g.id} draggable onDragStart={() => setGalDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropGal(i)} className="relative group rounded-lg overflow-hidden border border-border cursor-move" data-testid={`gallery-item-${g.id}`}>
                <img src={g.image} alt={g.caption || ""} className="w-full h-24 object-cover" />
                <button onClick={() => delGallery(g.id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`del-gallery-${g.id}`}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-5 sm:p-7 space-y-4 mt-5" data-testid="danger-zone-card">
        <div>
          <h3 className="font-heading font-semibold text-lg text-destructive">Danger zone</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {isOwner
              ? "Permanently delete your account and this entire club — teams, athletes, payments, messages and all data. This cannot be undone."
              : "Permanently delete your account. This cannot be undone."}
          </p>
        </div>
        <div className="sm:max-w-sm">
          <Label>Type <span className="font-mono font-semibold">DELETE</span> to confirm</Label>
          <Input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder="DELETE" className="mt-1.5 h-11" data-testid="delete-account-confirm-input" />
        </div>
        <Button variant="destructive" disabled={delConfirm.trim() !== "DELETE" || deleting} onClick={deleteAccount} data-testid="delete-account-btn">
          {deleting ? "Deleting…" : isOwner ? "Delete account & club" : "Delete my account"}
        </Button>
      </div>
    </div>
  );
}
